import type { StorageLike } from '@classgo/app-kit';
import type { TenantSession } from '@classgo/shared';

const key = (tenantId: string) => `classgo.sede.${tenantId}`;

/**
 * Which sede to show first: the remembered one for this tenant if it still exists, else the main
 * sede, else the first.
 *
 * @param {TenantSession['sedes']} sedes -- The sedes the session lists.
 * @param {string} tenantId -- The tenant the preference belongs to.
 * @param {StorageLike} storage -- Where the preference lives (`localStorage`).
 * @returns The sede id, or `null` when the tenant has none.
 */
export const initialSedeId = (
  sedes: TenantSession['sedes'],
  tenantId: string,
  storage: StorageLike,
): string | null => {
  let remembered: string | null = null;
  try {
    remembered = storage.getItem(key(tenantId));
  } catch {
    remembered = null;
  }
  if (remembered && sedes.some((sede) => sede.id === remembered)) return remembered;
  return sedes.find((sede) => sede.isMain)?.id ?? sedes[0]?.id ?? null;
};

/**
 * Remembers the chosen sede for the tenant.
 *
 * @param {string} tenantId -- The tenant.
 * @param {string} sedeId -- The chosen sede.
 * @param {StorageLike} storage -- Where the preference lives.
 */
export const rememberSede = (tenantId: string, sedeId: string, storage: StorageLike) => {
  try {
    storage.setItem(key(tenantId), sedeId);
  } catch {
    // A blocked storage only costs the memory of the choice.
  }
};
